import { forwardRef, useContext, useEffect } from 'react';
import { combineClassNames } from '../../Utils/ClassnameUtils';
import GWPDrawerContext from './GWPDrawerContext';

export type GWPDrawerFramePadding = 'default' | 'dense' | 'none';

export interface GWPDrawerFrameProps extends Omit<React.ComponentPropsWithoutRef<'div'>, 'title'> {
	title?: React.ReactNode;
	description?: React.ReactNode;
	footer?: React.ReactNode;
	leftActions?: React.ReactNode;
	rightActions?: React.ReactNode;
	padding?: GWPDrawerFramePadding;
	bodyClassName?: string;
	footerClassName?: string;
}

const GWPDrawerFrame = forwardRef<HTMLDivElement, GWPDrawerFrameProps>((props, ref) => {
	const {
		title,
		description,
		footer,
		leftActions,
		rightActions,
		padding = 'default',
		className,
		bodyClassName,
		footerClassName,
		children,
		...rest
	} = props;

	const drawerContext = useContext(GWPDrawerContext);
	const hasHeader = title !== undefined || description !== undefined;
	const hasMain = hasHeader || children !== undefined;
	const hasFooter = footer !== undefined || leftActions !== undefined || rightActions !== undefined;

	useEffect(() => {
		drawerContext?.registerTitle(title !== undefined);
		drawerContext?.registerDescription(description !== undefined);

		return () => {
			drawerContext?.registerTitle(false);
			drawerContext?.registerDescription(false);
		};
	}, [description, drawerContext, title]);

	return (
		<div ref={ref} className={combineClassNames('gwp-drawer-frame', className)} {...rest}>
			{hasMain && (
				<div className="gwp-drawer-frame-main">
					{hasHeader && (
						<div className="gwp-drawer-frame-header">
							{title !== undefined && (
								<h2 id={drawerContext?.titleId} className="gwp-drawer-frame-title">
									{title}
								</h2>
							)}
							{description !== undefined && (
								<p id={drawerContext?.descriptionId} className="gwp-drawer-frame-description">
									{description}
								</p>
							)}
						</div>
					)}

					{children !== undefined && (
						<div className={combineClassNames('gwp-drawer-frame-body', padding, bodyClassName)}>{children}</div>
					)}
				</div>
			)}

			{hasFooter && (
				<div className={combineClassNames('gwp-drawer-frame-footer', footerClassName)}>
					{footer !== undefined ? (
						footer
					) : (
						<>
							<div className="gwp-drawer-frame-footer-left">{leftActions}</div>
							<div className="gwp-drawer-frame-footer-right">{rightActions}</div>
						</>
					)}
				</div>
			)}
		</div>
	);
});

GWPDrawerFrame.displayName = 'GWPDrawerFrame';

export default GWPDrawerFrame;
